import { useQuery } from '@tanstack/react-query';
import { request } from '../api/client';
import { useAuthStore } from '../store/auth';

/** Work the reader must do, vs work of theirs that is sitting with somebody else. */
export type WorkGroup = 'ACT' | 'WAIT';
export type WorkTone = 'urgent' | 'normal' | 'info';
/** What KIND of work a row is — drives the coloured subject tile, not its urgency. */
export type WorkCategory = 'money' | 'orders' | 'stock' | 'people' | 'service' | 'general';

export interface WorkItem {
  key: string;
  group: WorkGroup;
  category: WorkCategory;
  /** Rupees riding on this row. Absent where money is not the point (an attendance fix, a KYC check). */
  amount?: number;
  /** 0 is legal — a yes/no row like "mark your attendance" has no quantity. */
  count: number;
  /** A plain, complete, imperative sentence. Rendered as-is; the client never re-words it. */
  title: string;
  hint: string;
  link: string;
  icon: string;
  tone: WorkTone;
  /** Age of the oldest thing behind this row, in days. */
  oldestDays?: number;
}

export interface Worklist {
  name: string;
  items: WorkItem[];
  totalAct: number;
  totalWait: number;
}

/**
 * "My Work" — everything still waiting on the signed-in user, already written as sentences.
 *
 * The server does all the wording and grouping (see worklist.module.ts) so the modal and the page
 * cannot drift apart, and so the phrasing can be corrected without shipping a frontend build.
 *
 * Polled on the same 60s cadence as the navbar badges: the two are the same underlying numbers, and
 * a user who clears an approval expects both the badge and this list to settle at the same moment.
 */
export function useWorklist(enabled = true) {
  const token = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ['worklist'],
    queryFn: () => request<Worklist>({ url: '/worklist' }),
    enabled: enabled && !!token,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}
